const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

// Soft parallax on the hero product image
const HERO_PARALLAX_CLASSNAME = 'hero-parallax';

function initializeHeroParallax() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const heroElements = Array.from(document.getElementsByClassName(HERO_PARALLAX_CLASSNAME));
  if (heroElements.length === 0) return;

  const maxOffset = 14; // px, either direction

  heroElements.forEach((element) => {
    const image = element.querySelector('.product__media img');
    if (!image) return;

    let elementIsVisible = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    const applyOffset = () => {
      const offset = ((percentageSeen(element) - 50) / 50) * maxOffset;
      image.style.setProperty('--parallax-y', `${offset.toFixed(1)}px`);
    };

    applyOffset();

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;
        applyOffset();
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

// Count-up for real spec numbers called out in comparison table cells (e.g. "6 opzetstukken", "200 minuten")
const COUNT_UP_CELL_SELECTOR = '.product-comparison__cell--us';
const COUNT_UP_KEYWORD_PATTERN = /(\d[\d.,]*)[^\d]{0,20}(opzetstuk|minuten)/i;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function initializeCountUp() {
  const cells = Array.from(document.querySelectorAll(COUNT_UP_CELL_SELECTOR));
  if (cells.length === 0) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const targets = [];

  cells.forEach((cell) => {
    const text = cell.textContent;
    const match = text.match(COUNT_UP_KEYWORD_PATTERN);
    if (!match) return;

    const numberText = match[1];
    const target = parseInt(numberText.replace(/[.,]/g, ''), 10);
    if (Number.isNaN(target)) return;

    const startIndex = match.index;
    const endIndex = startIndex + numberText.length;
    const before = text.slice(0, startIndex);
    const after = text.slice(endIndex);

    const numberSpan = document.createElement('span');
    numberSpan.className = 'count-up-number';
    numberSpan.textContent = reducedMotion ? numberText : '0';

    cell.textContent = '';
    cell.append(before, numberSpan, after);

    if (!reducedMotion) {
      targets.push({ numberSpan, target });
    }
  });

  if (targets.length === 0 || reducedMotion) return;

  const wrapper = document.querySelector('.product-comparison__wrapper') || document;
  let hasAnimated = false;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !hasAnimated) {
        hasAnimated = true;
        runCountUp(targets);
        observer.disconnect();
      }
    });
  });
  observer.observe(wrapper);
}

function runCountUp(targets) {
  const duration = 900;
  const start = performance.now();

  function tick(now) {
    const progress = Math.min((now - start) / duration, 1);
    const eased = easeOutCubic(progress);

    targets.forEach(({ numberSpan, target }) => {
      numberSpan.textContent = Math.round(target * eased);
    });

    if (progress < 1) {
      requestAnimationFrame(tick);
    }
  }

  requestAnimationFrame(tick);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  initializeHeroParallax();
  initializeCountUp();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
